import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../Button/Button';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
  totalItems?: number;
  pageSize?: number;
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
  itemLabel?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  className = '',
  totalItems,
  pageSize,
  pageSizeOptions = [10, 25, 50, 100],
  onPageSizeChange,
  itemLabel = 'rows',
}) => {
  const getPageNumbers = (): number[] => {
    const pages: number[] = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  const showRange = typeof totalItems === 'number' && typeof pageSize === 'number';
  const firstItem = showRange && totalItems! > 0 ? (currentPage - 1) * pageSize! + 1 : 0;
  const lastItem = showRange ? Math.min(currentPage * pageSize!, totalItems!) : 0;

  if (totalPages <= 1 && !onPageSizeChange && !showRange) return null;

  return (
    <div className={`flex flex-col md:flex-row items-center justify-between gap-3 ${className}`}>
      <div className="flex items-center gap-3 text-sm text-gray-600">
        {showRange && (
          <span>
            Showing <strong>{firstItem.toLocaleString()}</strong>–
            <strong>{lastItem.toLocaleString()}</strong> of{' '}
            <strong>{totalItems!.toLocaleString()}</strong> {itemLabel}
          </span>
        )}
        {onPageSizeChange && pageSize !== undefined && (
          <label className="flex items-center gap-1">
            <span className="text-gray-500">Per page</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm bg-white"
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => onPageChange(currentPage - 1)}
            leftIcon={<ChevronLeft className="w-4 h-4" />}
          >
            Previous
          </Button>

          <div className="flex space-x-1">
            {getPageNumbers().map((page) => (
              <Button
                key={page}
                variant={page === currentPage ? 'primary' : 'outline'}
                size="sm"
                onClick={() => onPageChange(page)}
              >
                {page}
              </Button>
            ))}
          </div>

          <Button
            variant="outline"
            size="sm"
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(currentPage + 1)}
            leftIcon={<ChevronRight className="w-4 h-4" />}
          >
            Next
          </Button>

          <span className="text-sm text-gray-500 ml-2">
            Page {currentPage} of {totalPages}
          </span>
        </div>
      )}
    </div>
  );
};
